// stores/productStore.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAccsProductService } from '@/services/accs_product.service'

interface ProjectOption {
    projectId: string
    customPriceId: string
}
export const useAccsProductStore = defineStore('accsProduct', () => {
    // Initialize the design project service for database operations
    const { fetchBySku } = useAccsProductService()

    // Map of SKU to designer project option ID for caching
    const designProjectOptionIdMap = ref<Map<string, ProjectOption>>(new Map())
    const loading = ref(false)
    const error = ref<Error | null>(null)

    const loadMagentoProduct = async (sku: string): Promise<ProjectOption | null> => {
        // Early return if already cached
        if (designProjectOptionIdMap.value.has(sku)) {
            return designProjectOptionIdMap.value.get(sku) ?? null
        }

        loading.value = true
        error.value = null

        try {
            const product = await fetchBySku(sku)

            const productOption = product?.inputOptions?.find(
                (opt) => opt.title === 'DESIGN_PROJECT_ID'
            )
            const customPriceOption = product?.inputOptions?.find(
                (opt) => opt.title === 'custom_price'
            )

            if (!productOption || !customPriceOption) {
                error.value = new Error(
                    'Missing required product input options: DESIGN_PROJECT_ID or custom_price'
                )
                return null
            }

            const projectId = productOption.id
            const customPriceId = customPriceOption.id
            const result = { projectId, customPriceId: customPriceId }
            // Cache the result in the map
            if (result) {
                designProjectOptionIdMap.value.set(sku, result)
            }

            return result
        } finally {
            loading.value = false
        }
    }

    return { designProjectOptionIdMap, loading, error, loadMagentoProduct }
})
